'use client';

import { useMemo, useState } from 'react';

import {
  useCreateDirectChatMessageMutation,
  useCreateGeneralChatMessageMutation,
  useCreateJobChatMessageMutation,
} from '@/features/chat/api/chatApi';
import {
  useLazyGetJobDeliverablesQuery,
  useLazyGetJobsQuery,
} from '@/features/jobs/api/jobsApi';
import { useLazyGetTasksQuery } from '@/features/tasks/api/tasksApi';
import { useLazyGetWorkspaceMembersQuery } from '@/features/workspaces/api/workspacesApi';
import { useCanWorkspaceAction } from '@/features/workspaces/hooks';
import { RichTextEditor } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './ChatPanel.module.scss';

import type { ActiveChat } from '@/app/(protected)/[workspace_slug]/chat/ChatContext';
import type {
  ChatContextTagPayload,
  ChatMessage,
  CreateChatMessagePayload,
} from '@/features/chat/types';
import type { Job, JobDeliverable } from '@/features/jobs/types';
import type { Task } from '@/features/tasks/types';
import type { WorkspaceMemberItem } from '@/features/workspaces/types';
import type { RichTextEditorSuggestionConfig } from '@web/ui';

function getRows<T>(data: { results?: T[] } | T[] | null | undefined) {
  if (Array.isArray(data)) {
    return data;
  }

  return data?.results ?? [];
}

function isAggregateChat(activeChat: ActiveChat) {
  return ['stream', 'unread', 'mentions'].includes(activeChat.type);
}

function getAuthorName(message: ChatMessage) {
  const user = message.author_user;
  if (!user) {
    return message.author_type === 'system' ? 'Net Net' : 'Unknown user';
  }

  return (
    user.display_name ||
    `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ||
    user.email ||
    'Unknown user'
  );
}

function getUserDisplayName(member: WorkspaceMemberItem) {
  return (
    `${member.user.first_name ?? ''} ${member.user.last_name ?? ''}`.trim() ||
    member.user.email ||
    'Unknown user'
  );
}

function getJobDescription(job: Job) {
  const client =
    job.company_contact?.name ??
    job.person_contact?.full_name ??
    `${job.person_contact?.first_name ?? ''} ${
      job.person_contact?.last_name ?? ''
    }`.trim();

  return [client, job.status].filter(Boolean).join(' • ');
}

function getTaskDescription(task: Task) {
  return [task.job?.name, task.deliverable?.name, task.status]
    .filter(Boolean)
    .join(' • ');
}

function getDeliverableDescription(deliverable: JobDeliverable) {
  return ['Deliverable', deliverable.status].filter(Boolean).join(' • ');
}

const CANONICAL_WORK_REFERENCE_REGEX =
  /^\[\[(job|deliverable|task):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\]\]$/i;

function hasInvalidWorkReferenceToken(value: string) {
  const tokens = value.match(/\[\[[^\]]+\]\]/g) ?? [];

  return tokens.some((token) => !CANONICAL_WORK_REFERENCE_REGEX.test(token));
}

export function ChatComposer({
  activeChat,
  replyingToMessage,
  onCancelReply,
  onCreateOptimisticMessage,
  onSettleOptimisticMessage,
  contextTag,
}: {
  activeChat: ActiveChat;
  replyingToMessage?: ChatMessage | null;
  contextTag?: ChatContextTagPayload;
  onCancelReply?: () => void;
  onCreateOptimisticMessage?: (
    payload: CreateChatMessagePayload,
    parentMessage?: ChatMessage | null,
    displayHtml?: string,
  ) => string;
  onSettleOptimisticMessage?: (
    optimisticId: string,
    message?: ChatMessage | null,
  ) => void;
}) {
  const [editorKey, setEditorKey] = useState(0);
  const [createGeneralMessage, generalMessageState] =
    useCreateGeneralChatMessageMutation();
  const [createJobMessage, jobMessageState] = useCreateJobChatMessageMutation();
  const [createDirectMessage, directMessageState] =
    useCreateDirectChatMessageMutation();
  const [getWorkspaceMembers] = useLazyGetWorkspaceMembersQuery();
  const [getJobs] = useLazyGetJobsQuery();
  const [getJobDeliverables] = useLazyGetJobDeliverablesQuery();
  const [getTasks] = useLazyGetTasksQuery();
  const canCreateMessages = useCanWorkspaceAction(
    'manage_own_messages',
    'create',
    false,
  );
  const isAggregate = isAggregateChat(activeChat);
  const isReadonly = Boolean(activeChat.isArchived || activeChat.isReadOnly);
  const isMissingWriteTarget =
    (activeChat.type === 'job_channel' && !activeChat.jobId) ||
    (activeChat.type === 'general_channel' && activeChat.id === 'general');
  const isSending =
    generalMessageState.isLoading ||
    jobMessageState.isLoading ||
    directMessageState.isLoading;
  const isComposerDisabled =
    !canCreateMessages || isReadonly || isMissingWriteTarget || isSending;

  const isReplying = Boolean(replyingToMessage);
  const suggestions = useMemo<RichTextEditorSuggestionConfig[]>(
    () => [
      {
        trigger: '@',
        label: 'People',
        getItems: async (query) => {
          const response = await getWorkspaceMembers({
            status: 'active',
            query,
            page: 1,
            page_size: 25,
          }).unwrap();

          return (response.data?.results ?? []).map((member) => {
            const label = getUserDisplayName(member);

            return {
              id: member.user.id,
              label,
              description: member.role?.name ?? 'Person',
              canonicalValue: `@[${member.user.id}]`,
            };
          });
        },
      },
      {
        trigger: '[[',
        label: 'Work references',
        getItems: async (query) => {
          const shouldLoadDeliverables =
            activeChat.type === 'job_channel' && Boolean(activeChat.jobId);
          const [jobsResponse, tasksResponse, deliverablesResponse] =
            await Promise.all([
              getJobs({
                query,
                status: 'active,completed,pending',
                page: 1,
                page_size: 25,
              }).unwrap(),
              getTasks({
                query,
                task_type: 'all',
                page: 1,
                page_size: 25,
              }).unwrap(),
              shouldLoadDeliverables
                ? getJobDeliverables({
                    jobId: activeChat.jobId!,
                    params: {
                      page_size: 25,
                    },
                  }).unwrap()
                : Promise.resolve(null),
            ]);

          const jobs =
            jobsResponse.data?.results.map((job) => ({
              id: `job-${job.id}`,
              label: job.name,
              description: getJobDescription(job),
              canonicalValue: `[[job:${job.id}]]`,
            })) ?? [];

          const tasks =
            tasksResponse.data?.results.map((task) => ({
              id: `task-${task.id}`,
              label: task.title ?? 'Untitled task',
              description: getTaskDescription(task),
              canonicalValue: `[[task:${task.id}]]`,
            })) ?? [];

          const deliverables =
            getRows<JobDeliverable>(deliverablesResponse?.data)
              .filter((deliverable) =>
                deliverable.name.toLowerCase().includes(query.toLowerCase()),
              )
              .map((deliverable) => ({
                id: `deliverable-${deliverable.id}`,
                label: deliverable.name,
                description: getDeliverableDescription(deliverable),
                canonicalValue: `[[deliverable:${deliverable.id}]]`,
              })) ?? [];

          return [...deliverables, ...jobs, ...tasks];
        },
      },
    ],
    [
      activeChat.jobId,
      activeChat.type,
      getJobDeliverables,
      getJobs,
      getTasks,
      getWorkspaceMembers,
    ],
  );

  if (isAggregate) {
    return (
      <div className={styles.chatPanel__composer}>
        <div className={styles.chatPanel__composerReadOnly}>
          Choose Reply on a message to respond from {activeChat.title}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chatPanel__composer}>
      {replyingToMessage ? (
        <div className={styles.chatPanel__replyingBar}>
          <span>
            Replying to <strong>{getAuthorName(replyingToMessage)}</strong> in{' '}
            {activeChat.title}
          </span>
          <button type="button" onClick={onCancelReply}>
            Cancel
          </button>
        </div>
      ) : null}
      <RichTextEditor
        key={editorKey}
        placeholder={
          isComposerDisabled
            ? 'Chat is read-only.'
            : isReplying
              ? 'Write a reply...'
              : 'Write a message...'
        }
        disabled={isComposerDisabled}
        showSubmitButton
        submitAriaLabel="Send message"
        isSubmitDisabled={isComposerDisabled}
        footerLeft="[[ work references   @ people"
        footerRight={isReplying ? 'Reply mode' : 'Top-level message'}
        suggestions={suggestions}
        onSubmit={async (value) => {
          if (isComposerDisabled || value.isEmpty) {
            return;
          }

          if (hasInvalidWorkReferenceToken(value.canonicalHtml)) {
            notify({
              type: 'error',
              message: 'Choose work references from the suggestions list.',
            });
            return;
          }

          const payload = {
            body: value.canonicalHtml,
            body_format: 'html' as const,
            parent_message_id: replyingToMessage?.id,
            context_tag: replyingToMessage ? undefined : contextTag,
          };
          const optimisticId = onCreateOptimisticMessage?.(
            payload,
            replyingToMessage,
            value.html,
          );

          try {
            let nextMessage: ChatMessage | null | undefined;

            if (activeChat.type === 'job_channel' && activeChat.jobId) {
              const response = await createJobMessage({
                jobId: activeChat.jobId,
                payload,
              }).unwrap();
              nextMessage = response.data;
            } else if (activeChat.type === 'direct_message') {
              const response = await createDirectMessage({
                conversationId: activeChat.id,
                payload,
              }).unwrap();
              nextMessage = response.data;
            } else {
              const response = await createGeneralMessage(payload).unwrap();
              nextMessage = response.data;
            }

            if (optimisticId) {
              onSettleOptimisticMessage?.(optimisticId, nextMessage ?? null);
            }

            setEditorKey((current) => current + 1);
            onCancelReply?.();
          } catch (error) {
            if (optimisticId) {
              onSettleOptimisticMessage?.(optimisticId, null);
            }

            notify({ type: 'error', message: getErrorMessage(error) });
          }
        }}
      />
    </div>
  );
}
